/**
 * ops SLO ダッシュボードの期間（月 / 四半期）ロジック。SLO タイムラインの期間ピッカーが選べる
 * 期間の境界（起票日基準の開始日）と、airCloset 会計四半期のラベルを **JST 基準** で算出する。
 * backend の SLO クエリが `DATE_TRUNC(CURRENT_DATE('Asia/Tokyo'), MONTH/QUARTER)` で期間を切るのと
 * 同じ JST 暦にそろえるため、`now` は getJstYmd で JST 暦日へ正規化してから期間を作る（プロセス /
 * ブラウザのローカル TZ に依存しない）。SLA 営業日計算（sla.ts）と同じく、ops の SLO 関連ロジックは
 * この ops-core を単一 source とし、frontend の表示（SLO ダッシュボード / タイムライン）が委譲する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops SLO ダッシュボードの期間ピッカー（直近 12 ヶ月 / 8 四半期）と会計四半期ラベルを JST 基準で算出する共有ロジック
 * @graph-connects none
 */
import { getJstYmd } from '@cortex/jst-calendar';
import { JST_OFFSET_MS } from './jst.js';
/**
 * 1 期間の長さ（月数）。月 = 1、四半期 = 3。SLO の期間絞り込み（product-api の SQL）とステート履歴の
 * 期間窓（`opsSloPeriodUtcWindow`）が同じ長さを使うための単一 source。
 * @graph-connects none
 */
export function opsSloPeriodMonths(granularity) {
    return granularity === 'quarter' ? 3 : 1;
}
/**
 * JST の月（1-based）が属する期間の開始月（1-based）。月単位はその月、四半期は 1 / 4 / 7 / 10 月。
 * @graph-connects none
 */
function periodStartMonth(granularity, month) {
    return granularity === 'month' ? month : Math.floor((month - 1) / 3) * 3 + 1;
}
/** 期間ラベル用の月名（英略記、0-based）。 @graph-connects none */
const MONTH_ABBR = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
/** 2 桁ゼロ埋め。 @graph-connects none */
function pad2(n) {
    return String(n).padStart(2, '0');
}
/**
 * 通し月インデックス（year*12 + month0）から月の選択肢（value=月初 YYYY-MM-01、label="Aug 2026"）を作る。
 * 期間ピッカー（`opsSloPeriodOptions`）とトレンド（`opsSloTrendMonths`）が同じ月表現を共有するための単一
 * source（月の綴り・境界を二重定義しない）。
 * @graph-connects none
 */
function monthOptionAt(monthIndex) {
    const yy = Math.floor(monthIndex / 12);
    const mm0 = monthIndex - yy * 12; // 0-based month
    return {
        value: `${String(yy)}-${pad2(mm0 + 1)}-01`,
        label: `${MONTH_ABBR[mm0] ?? ''} ${String(yy)}`,
    };
}
/**
 * 暦の四半期開始（年 + 開始月 1/4/7/10）を airCloset の会計四半期ラベルへ変換する。会計年度は 7 月開始で
 * 終了暦年で命名（Jul2026–Jun2027 = FY2027）。例: 2026 年 7 月開始 → "2027 Q1"、2027 年 1 月開始 → "2027 Q3"。
 * @graph-connects none
 */
export function fiscalQuarterLabel(calendarYear, startMonth) {
    const quarterByStartMonth = { 7: 1, 10: 2, 1: 3, 4: 4 };
    const fiscalQuarter = quarterByStartMonth[startMonth] ?? 1;
    const fiscalYear = startMonth >= 7 ? calendarYear + 1 : calendarYear;
    return `${String(fiscalYear)} Q${String(fiscalQuarter)}`;
}
/**
 * 期間ピッカーの選択肢を新しい順で作る。value=期間開始日（YYYY-MM-DD）、label=表示名（"Aug 2026" /
 * "2027 Q1"）。月は直近 12、四半期は直近 8 を返し、先頭が現在の期間。`now` は JST 暦日に正規化してから
 * 数えるため、UTC ランタイム / 国外ブラウザでも backend の JST 期間境界と一致する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business SLO タイムラインの期間ピッカー選択肢（直近 12 ヶ月 / 8 四半期）を JST 基準で新しい順に返す
 * @graph-connects fiscalQuarterLabel [calls] 四半期の会計ラベル整形
 */
export function opsSloPeriodOptions(granularity, now = new Date()) {
    const { year, month } = getJstYmd(now); // month は 1-based
    if (granularity === 'month') {
        const baseMonthIndex = year * 12 + (month - 1); // 0-based の通し月インデックス
        return Array.from({ length: 12 }, (_, i) => monthOptionAt(baseMonthIndex - i));
    }
    const options = [];
    let qYear = year;
    let quarter = (periodStartMonth('quarter', month) - 1) / 3; // 0-based quarter
    for (let i = 0; i < 8; i += 1) {
        const startMonthNum = quarter * 3 + 1;
        options.push({
            value: `${String(qYear)}-${pad2(startMonthNum)}-01`,
            label: fiscalQuarterLabel(qYear, startMonthNum),
        });
        quarter -= 1;
        if (quarter < 0) {
            quarter = 3;
            qYear -= 1;
        }
    }
    return options;
}
/**
 * SLO ボリューム推移チャートで選べる遡り月数（直近 N ヶ月）。frontend のトグルと backend のクエリ
 * バリデーションが同じ値集合を参照する単一 source。
 * @graph-connects none
 */
export const OPS_SLO_TREND_MONTH_CHOICES = [6, 12];
/**
 * 直近 N ヶ月窓の最古月の通し月インデックス（year*12 + month0 − (months−1)）を JST 基準で返す。
 * `opsSloTrendMonths` と `opsSloTrendWindowStart` が窓の起点を同じ式で共有する（drift 防止）。
 * @graph-connects none
 */
function trendOldestMonthIndex(months, now) {
    const { year, month } = getJstYmd(now); // month は 1-based
    return year * 12 + (month - 1) - (months - 1);
}
/**
 * SLO ボリューム推移チャートの月バケットを **古い順（チャート X 軸の並び）** で N ヶ月ぶん返す。末尾が現在の
 * 月。`now` は JST 暦日へ正規化してから数えるため、UTC ランタイム / 国外ブラウザでも backend の JST 月境界と
 * 一致する。frontend が各月へ依頼を振り分けて件数 / 工数 / SLO 達成率を集計する土台。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business SLO ボリューム推移チャートの月バケット（直近 N ヶ月・古い順）を JST 基準で返す共有ロジック
 * @graph-connects none
 */
export function opsSloTrendMonths(months, now = new Date()) {
    const oldestMonthIndex = trendOldestMonthIndex(months, now);
    return Array.from({ length: months }, (_, i) => {
        const opt = monthOptionAt(oldestMonthIndex + i); // 古い順（末尾が現在）
        return { key: opt.value.slice(0, 7), start: opt.value, label: opt.label };
    });
}
/**
 * SLO ボリューム推移の対象窓の下限（最古月の月初 YYYY-MM-01）を返す。backend が「直近 N ヶ月に起票された
 * 依頼」を 1 クエリで絞るための開始日。窓の月集合（`opsSloTrendMonths`）の先頭と必ず一致する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business SLO ボリューム推移の対象窓の下限（最古月の月初）を JST 基準で返す共有ロジック
 * @graph-connects opsSloTrendMonths [calls] 月バケット列の先頭（最古月）を取得
 */
export function opsSloTrendWindowStart(months, now = new Date()) {
    return monthOptionAt(trendOldestMonthIndex(months, now)).value;
}
/**
 * ISO 日時（起票日）を JST の月バケットキー "YYYY-MM" へ写す。パースできなければ null（呼び出し側で除外）。
 * `opsSloTrendMonths` の `key` と同じ JST 基準で作るため、依頼をチャートの月バケットへ正しく振り分けられる。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 起票日時を JST の月バケットキー（YYYY-MM）へ正規化する共有ロジック（SLO 推移の月別集計に使う）
 * @graph-connects none
 */
export function jstYearMonthKey(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return null;
    const { year, month } = getJstYmd(d);
    return `${String(year)}-${pad2(month)}`;
}
/**
 * SLO の期間（起票日基準・JST の月 / 四半期）を UTC の半開区間 [from, to)（ISO）にする。start（YYYY-MM-DD、
 * 期間初日）省略時は now を含む期間。product-api の SLO 一覧が JST 暦日で切る期間と同じ集合になる
 * （native 依頼の起票日は起票時刻の JST 暦日）ので、起票時刻の TIMESTAMP 列で同じ期間を絞り込める。
 * @graph-connects opsSloPeriodMonths [calls] 期間の長さ
 * @graph-connects periodStartMonth [calls] start 省略時の期間開始月
 */
export function opsSloPeriodUtcWindow(granularity, start, now = new Date()) {
    const today = getJstYmd(now);
    const [year = today.year, month = 1, day = 1] = start === undefined
        ? [today.year, periodStartMonth(granularity, today.month), 1]
        : start.split('-').map(Number);
    // JST 0 時 = UTC で前日 15 時。月の繰り上がりは Date.UTC が吸収する。
    const at = (monthOffset) => new Date(Date.UTC(year, month - 1 + monthOffset, day) - JST_OFFSET_MS).toISOString();
    return { from: at(0), to: at(opsSloPeriodMonths(granularity)) };
}
//# sourceMappingURL=slo-period.js.map